import { ShipBuilderState } from "../../types/ship";
import { ValidationResult } from "../../types/validation";
import { ShipBuilderStepId } from "../../constants/ship";
import { DriveType } from "../../types/drive";

export interface ValidationError {
  message: string;
  field?: string;
}

export interface ValidationWarning {
  message: string;
  field?: string;
}

/**
 * Validates a specific ship builder step
 * @param state - The current ship builder state
 * @param step - The step to validate
 * @returns Array of validation errors
 */
export const validateShipBuilderStep = (
  state: ShipBuilderState,
  step: ShipBuilderStepId,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  switch (step) {
    case "initial":
      if (!state.shipSize || state.shipSize <= 0) {
        errors.push({
          message: "Ship size must be greater than 0",
          field: "shipSize",
        });
      }
      break;

    case "hull-and-armor":
      if (!state.hull) {
        errors.push({
          message: "Hull must be selected",
          field: "hull",
        });
      }
      // Armor is optional - null means "None" is selected
      break;

    case "drives":
      const hasJumpDrive = state.drives?.some((d) => d.type === DriveType.JUMP);
      const hasManeuverDrive = state.drives?.some(
        (d) => d.type === DriveType.MANEUVER,
      );

      if (hasJumpDrive && !hasManeuverDrive) {
        errors.push({
          message: "Jump drives require maneuver drives to function",
          field: "drives",
        });
      }

      if (hasManeuverDrive && !hasJumpDrive) {
        errors.push({
          message: "Maneuver drive without jump drive may be inefficient",
          field: "drives",
        });
      }
      break;

    case "power":
      // Power plant validation - required for ships
      if (!state.powerPlant || !state.powerPlant.type) {
        errors.push({
          message: "Power plant must be selected for ships",
          field: "powerPlant",
        });
      }
      break;

    case "fuel":
      // Fuel validation can be added here if needed
      break;

    case "bridge":
      // Bridge step validation - bridges are required for ships
      if (!state.bridge) {
        errors.push({
          message: "Bridge must be selected for ships",
          field: "bridge",
        });
      }
      break;

    case "computer":
      // Computer validation - required for ships
      if (!state.computer || (!state.computer.primary && !state.computer.backup)) {
        errors.push({
          message: "At least one computer must be installed for ships",
          field: "computer",
        });
      }

      if (state.computer && state.computer.primary && state.computer.backup) {
        const primaryProcessing = state.computer.primary.processing;
        const backupProcessing = state.computer.backup.processing;

        if (backupProcessing >= primaryProcessing) {
          errors.push({
            message:
              "Backup computer must have lower processing power than primary computer",
            field: "computer.backup",
          });
        }
      }
      break;

    case "sensors":
      // Sensors validation can be added here if needed
      break;

    case "weapons":
      // No specific validation for weapons in this step
      break;

    case "optional-systems":
      // Optional systems are optional, but if selected must be valid
      if (state.optionalSystems) {
        state.optionalSystems.forEach((system, index) => {
        if (!system.type) {
          errors.push({
            message: `Optional system ${index + 1} must have a type`,
            field: `optionalSystems[${index}].type`,
          });
        }
        // Solar Panels validation
        if (system.type === "power" && system.subtype === "solarPanels") {
          const hullType = state.hull?.configuration?.type;
          const allowed =
            (system.properties?.allowedHullTypes as string[]) || [];
          const disallowed =
            (system.properties?.disallowedHullTypes as string[]) || [];
          if (hullType && disallowed.includes(hullType)) {
            errors.push({
              message: `Solar Panels cannot be installed on hull type: ${hullType}`,
              field: `optionalSystems[${index}].hullType`,
            });
          }
          if (hullType && allowed.length > 0 && !allowed.includes(hullType)) {
            errors.push({
              message: `Solar Panels are not allowed on hull type: ${hullType}`,
              field: `optionalSystems[${index}].hullType`,
            });
          }
        }
      });
      }
      // Solar Coating hull option validation
      if (state.hull && state.hull.options) {
        const options = state.hull.options;
        const hasSolarCoating = options.some((o) => o.type === "solarCoating");
        const solarCoating = options.find((o) => o.type === "solarCoating");
        const percent =
          solarCoating && typeof solarCoating.percent === "number"
            ? solarCoating.percent
            : 0;
        const hullType = state.hull.configuration?.type;

        if (hasSolarCoating) {
          // Null hull configuration validation - check this first
          if (!state.hull.configuration) {
            errors.push({
              message:
                "Solar Coating cannot be installed without a valid hull configuration.",
              field: "hull.configuration",
            });
          }

          // Mutual exclusion: only radiationShielding allowed with solarCoating
          const otherOptions = options.filter(
            (o) => o.type !== "solarCoating" && o.type !== "radiationShielding",
          );
          if (otherOptions.length > 0) {
            errors.push({
              message:
                "Solar Coating cannot be combined with other hull options except Radiation Shielding.",
              field: "hull.options",
            });
          }
          // Percent limit
          if (percent > 40) {
            errors.push({
              message: "Solar Coating cannot cover more than 40% of the hull.",
              field: "hull.options",
            });
          }
          // Negative percent
          if (percent < 0) {
            errors.push({
              message: "Solar Coating percent cannot be negative.",
              field: "hull.options",
            });
          }
          // Hull type restriction
          const allowed = [
            "standard",
            "sphere",
            "closeStructure",
            "dispersedStructure",
          ];
          const disallowed = ["streamlined", "planetoid", "bufferedPlanetoid"];
          if (hullType && disallowed.includes(hullType)) {
            errors.push({
              message: `Solar Coating cannot be installed on hull type: ${hullType}`,
              field: "hull.options",
            });
          }
          if (hullType && allowed.length > 0 && !allowed.includes(hullType)) {
            errors.push({
              message: `Solar Coating is not allowed on hull type: ${hullType}`,
              field: "hull.options",
            });
          }
        }
      }
      break;

    case "crew":
      // Basic crew validation using the new crew system
      // For modules, crew requirements may be different or optional
      const crewOverrides = state.crewOverrides || {};
      const pilotCount = crewOverrides["Pilot"] || 0;
      const engineerCount = crewOverrides["Engineer"] || 0;
      
      // For ships, require basic crew
      if (pilotCount < 1) {
        errors.push({
          message: "At least one pilot is required for ships",
          field: "crewOverrides.Pilot",
        });
      }
      if (engineerCount < 1) {
        errors.push({
          message: "At least one engineer is required for ships",
          field: "crewOverrides.Engineer",
        });
      }
      break;

    case "staterooms":
      // Staterooms are optional, but if selected must be valid
      if (state.staterooms) {
        state.staterooms.forEach((stateroom, index) => {
        if (!stateroom.type) {
          errors.push({
            message: `Stateroom ${index + 1} must have a type`,
            field: `staterooms[${index}].type`,
          });
        }
      });
      }
      break;

    case "cargo":
      // Cargo is optional, but if selected must be valid
      if (state.cargo && state.cargo.space < 0) {
        errors.push({
          message: "Cargo space cannot be negative",
          field: "cargo.space",
        });
      }
      break;

    case "summary":
      // Check that final details are completed
      if (!state.finalDetails.name) {
        errors.push({
          message: "Ship name is required",
          field: "finalDetails.name",
        });
      }
      if (!state.finalDetails.manufacturer) {
        errors.push({
          message: "Manufacturer is required",
          field: "finalDetails.manufacturer",
        });
      }
      break;
  }

  return errors;
};

/**
 * Validates if a specific step is complete
 * @param state - The current ship builder state
 * @param step - The step to validate
 * @returns Validation result with success status and errors
 */
export const validateStep = (
  state: ShipBuilderState,
  step: ShipBuilderStepId,
): ValidationResult => {
  const errors: ValidationError[] = validateShipBuilderStep(state, step);

  return {
    isValid: errors.length === 0,
    errors: errors.map((error) => error.message),
  };
};

/**
 * Validates if a specific step is accessible for navigation
 * This is different from step completion - it checks if the step can be visited
 * @param state - The current ship builder state
 * @param step - The step to validate
 * @returns Validation result with success status and errors
 */
export const validateStepAccessibility = (
  state: ShipBuilderState,
  step: ShipBuilderStepId,
): ValidationResult => {
  // Allow free navigation after initial setup (ship size and tech level)
  // Users can navigate to any step once basic ship parameters are set
  const allowFreeNavigationAfterSetup = true;
  
  if (allowFreeNavigationAfterSetup) {
    // Only require ship size and tech level for all steps except "initial"
    if (step !== "initial") {
      if (!state.shipSize || state.shipSize <= 0) {
        return {
          isValid: false,
          errors: ["Ship size must be set before proceeding"],
        };
      }
      if (!state.techLevel || state.techLevel <= 0) {
        return {
          isValid: false,
          errors: ["Technology level must be set before proceeding"],
        };
      }
    }
    
    // If basic requirements are met, allow access to any step
    return {
      isValid: true,
      errors: [],
    };
  }

  // Fallback validation for when free navigation is disabled
  const errors: ValidationError[] = [];

  // ALL steps except "initial" require ship size and tech level to be set first
  if (step !== "initial") {
    if (!state.shipSize || state.shipSize <= 0) {
      errors.push({
        message: "Ship size must be set before proceeding",
        field: "shipSize",
      });
    }
    if (!state.techLevel || state.techLevel <= 0) {
      errors.push({
        message: "Technology level must be set before proceeding",
        field: "techLevel",
      });
    }
  }

  // Note: Step-specific prerequisites are removed to allow free navigation
  // Users can now access any step once ship size and tech level are set

  return {
    isValid: errors.length === 0,
    errors: errors.map((error) => error.message),
  };
};
